import { useCallback, useMemo } from 'react';
import { LocalAccount } from '../../../types/LocalAccount';
import { useBindLocalAccountsPopup } from './useBindLocalAccountsPopup';
import { useUnbindLocalAccountsPopup } from './useUnbindLocalAccountsPopup';
import { useExcludeLocalAccountsPopup } from './useExcludeLocalAccountsPopup';
import { usePricingModulesState } from '../../../../../context/contexts/PricingModuleContext';
import { isMfaModuleEnabled } from '../../../utils';
import useBulkResumeLocalAccountsProtection from './useBulkResumeLocalAccountsProtectionPopup';
import { BulkAction } from '../../BulkActions';
import { isUserPermitted } from '../../../../../TowerMenu/TowerMenuUtils';
import { ROLES } from '@silverfort-nodelibs/common/utils/consts';
import { useLocalAccounts } from '../../../context/LocalAccountsContext';

interface Params {
    selectedAccounts: LocalAccount[];
    onClose: () => void;
    onActionSuccess: () => void;
}

interface useLocalAccountActionsReturnType {
    actions: BulkAction[];
}

const useLocalAccountBulkActions = ({
    selectedAccounts,
    onClose,
    onActionSuccess
}: Params): useLocalAccountActionsReturnType => {
    const { userSession } = useLocalAccounts();
    const { pricingModulesState } = usePricingModulesState();
    const mfaModuleEnabled = isMfaModuleEnabled(pricingModulesState);
    const allAccountsBound = selectedAccounts.every((account) => account.boundAdEntity);
    const allAccountsExcluded = selectedAccounts.every((account) => {
        return account.excludeTill && account.excludeTill > new Date();
    });

    const { openBindLocalAccountsPopup } = useBindLocalAccountsPopup({
        accounts: selectedAccounts,
        onAccountsBound: useCallback(() => {
            onActionSuccess();
            onClose();
        }, [onActionSuccess, onClose])
    });

    const { openUnbindLocalAccountsPopup } = useUnbindLocalAccountsPopup({
        accounts: selectedAccounts,
        onAccountsUnbound: useCallback(() => {
            onActionSuccess();
            onClose();
        }, [onActionSuccess, onClose])
    });

    const { openExcludeLocalAccountsPopup } = useExcludeLocalAccountsPopup({
        accounts: selectedAccounts,
        onAccountsExcluded: useCallback(() => {
            onActionSuccess();
            onClose();
        }, [onActionSuccess, onClose])
    });

    const { resumeLocalAccountsProtection } = useBulkResumeLocalAccountsProtection({
        accounts: selectedAccounts,
        onProtectionResumed: useCallback(() => {
            onActionSuccess();
            onClose();
        }, [onActionSuccess, onClose])
    });

    const actions: BulkAction[] = useMemo(
        () => [
            {
                key: 'binding',
                label: allAccountsBound ? 'Unbind' : 'Bind',
                onClick: allAccountsBound ? openUnbindLocalAccountsPopup : openBindLocalAccountsPopup,
                disabled: !isUserPermitted(userSession, 'matchUsers', ROLES.EDITOR.value)
            },
            ...(mfaModuleEnabled
                ? [
                      {
                          key: 'exclusion',
                          label: allAccountsExcluded ? 'Resume protection' : 'Temporarily exclude',
                          onClick: allAccountsExcluded ? resumeLocalAccountsProtection : openExcludeLocalAccountsPopup,
                          disabled: !isUserPermitted(userSession, 'excludeUsers', ROLES.EDITOR.value)
                      }
                  ]
                : [])
        ],
        [
            allAccountsBound,
            allAccountsExcluded,
            openBindLocalAccountsPopup,
            openUnbindLocalAccountsPopup,
            openExcludeLocalAccountsPopup,
            resumeLocalAccountsProtection,
            mfaModuleEnabled
        ]
    );

    return { actions };
};

export default useLocalAccountBulkActions;
